// Updated Perplexity service to use Vercel API routes for security
import { supabase } from '../lib/supabase';

// Extend Window interface for rate limit sync
declare global {
  interface Window {
    updateRateLimit?: (rateLimitInfo: { remaining: number; resetTime: number }) => void;
  }
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface PerplexityResponse {
  text: string;
  citations?: string[];
  usage?: {
    tokens: number;
  };
  rateLimit?: {
    remaining: number;
    resetTime: number;
  };
}

class PerplexityService {
  private readonly backendUrl: string;

  constructor() {
    // Use local API routes for Vercel deployment, or external backend URL for development
    this.backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  }

  /**
   * Get chat completion through secure API proxy
   */
  async chat(messages: ChatMessage[]): Promise<PerplexityResponse> {
    if (!this.isAvailable()) {
      throw new Error('Perplexity service is not available. Please check your configuration.');
    }

    try {
      const authToken = await this.getAuthToken();
      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          messages,
          source: 'dream11-soccer'
        })
      });

      if (!response.ok) {
        let errorData;
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          // This is likely the HTML response causing the JSON parse error
          const textResponse = await response.text();
          throw new Error(`API returned HTML instead of JSON. Status: ${response.status}. Check if API route exists.`);
        }
        
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Note: We no longer sync rate limits from backend since we use frontend lifetime limits
      // The frontend enforces a strict 5-request lifetime limit per user
      
      return data;
    } catch (error) {
      console.error('Error calling Perplexity API:', error);
      throw error;
    }
  }

  /**
   * Get authentication token for backend verification
   */
  private async getAuthToken(): Promise<string> {
    try {
      // Only use Supabase JWT tokens for security - NO localStorage tokens
      if (supabase) {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (session?.access_token && !error) {
          return session.access_token;
        }
      }
      
      throw new Error('Authentication required - please sign in with Supabase');
    } catch (error) {
      throw new Error('Authentication required for AI features');
    }
  }

  /**
   * Check if the service is available
   */
  isAvailable(): boolean {
    return true; // Always available with local API routes
  }

  /**
   * Check backend health
   */
  async checkHealth(): Promise<boolean> {
    try {
      const healthUrl = this.backendUrl ? `${this.backendUrl}/api/health` : '/api/health';
      const response = await fetch(healthUrl);
      const data = await response.json();
      return data.status === 'healthy';
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }
}

export const perplexityService = new PerplexityService(); 